import { useNavigate } from 'react-router'
import { useMutation } from '@tanstack/react-query'

import useDialog from '@shared/hooks/src/useDialog'
import useQuery from '@shared/hooks/src/useQuery'
import { setQueriesData, setQueryData } from '@shared/providers/src/QueryClientProvider'
import API from '@shared/services/src/API'
import { mapCache, QK, UserRole } from '@shared/utils'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

export function useSystemUser(userId, options = {}) {
  return useQuery({
    queryKey: QK.admin.systemUsers.id(userId).details,
    queryFn: () => API.admin.systemUsers.id(userId).details(),
    select: (data) => {
      if (data.role === UserRole.Pharmacy) {
        return {
          ...data,
          pharmacies: data.pharmacies?.filter((p) => p.id !== data.pharmacy.id) || [],
        }
      }
      return data
    },
    ...options,
  })
}

export function useProfilePhotoUpdate(userId) {
  return useMutation({
    mutationFn: (data) => API.users.id(userId).photo.update(data),
    onSuccess: ({ url }) =>
      setQueriesData({ queryKey: QK.admin.systemUsers.id(userId).details }, (current) => ({
        ...current,
        profilePictureUrl: url,
        profilePictureThumbUrl: url,
      })),
  })
}

export function useProfilePhotoRemove(userId) {
  return useMutation({
    mutationFn: () => API.users.id(userId).photo.remove(),
    onSuccess: () =>
      setQueriesData({ queryKey: QK.admin.systemUsers.id(userId).details }, (current) => ({
        ...current,
        profilePictureUrl: undefined,
        profilePictureThumbUrl: undefined,
      })),
  })
}

export function useLoginAs(userId, { onConfirm } = {}) {
  const navigate = useNavigate()

  return useDialog({
    component: ConfirmationV2,
    props: ({ close }) => ({
      maxWidth: 'xs',
      variant: 'warning',
      title: 'Login as User',
      description: 'Are you sure you want to login as this user?',
      onReject: () => close(),
      onConfirm: () => {
        close()
        navigate(`/app/login-as?user_id=${userId}`)
        onConfirm?.()
      },
    }),
  })
}

export function useUserUnlock() {
  return useMutation({
    mutationFn: (id) => API.users.id(id).status.unlock(),
    onSuccess: (user, id) => {
      setQueryData(QK.admin.systemUsers.id(id).details, (old) => ({ ...old, ...user }))
    },
  })
}

export function useUserStatusUpdate() {
  return useMutation({
    mutationFn: ({ id, status }) => {
      if (status === 'active') return API.users.id(id).status.activate()
      if (status === 'disabled') return API.users.id(id).status.disable()
    },
    onSuccess: (user, { id, status }) => {
      setQueryData(QK.admin.systemUsers.id(id).details, (old) => ({ ...old, ...user }))
      setQueriesData(
        { queryKey: QK.admin.systemUsers.lists },
        mapCache((old) => (old.id === id ? { ...old, status } : old))
      )
      setQueriesData(
        { queryKey: QK.admin.providers.lists },
        mapCache((old) => (old.userId === id ? { ...old, status } : old))
      )
    },
  })
}
